import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { friendlyDeleteError } from '../utils/deleteError';
import { useDeleteConfirm } from '../hooks/useDeleteConfirm';
import { canDeleteForever } from '../utils/deleteGuard';
import AccountBar from './account/AccountBar';
import BreadcrumbBar, { type BreadcrumbAction } from './BreadcrumbBar';
import { useRole } from '../hooks/useRole';
import ArchivedToggle from './ui/ArchivedToggle';
import AccountMetrics from './account/AccountMetrics';
import { quoteMetrics } from './account/accountMetricSets';
import { useExport } from './header/exporters';
import { isArchiveView, toArchiveView, type ArchiveView } from '../utils/archiveFilter';
import LoadErrorBox from './ui/LoadErrorBox';
import idColumn from './ui/idColumn';
import { useNavigate } from 'react-router-dom';
import Icon from './Icon';
import DataTable, { Column, actionsColumn } from './DataTable';
import ConfirmDialog from './ui/ConfirmDialog';
import EmptyState from './EmptyState';
import { useNotifications } from './NotificationProvider';
import { newBillingDocPath } from '../utils/projectBilling';
import { formatMoney } from '../utils/currency';
import { loadClients, Client } from '../services/ClientService';
import {
  loadClientQuotes,
  sendClientQuote,
  acceptClientQuote,
  declineClientQuote,
  restoreClientQuote,
  deleteClientQuote,
  ClientQuote,
} from '../services/ClientQuoteService';
import { NEW_CLIENT_QUOTE_PATH, clientQuotePath } from '../utils/accountRoutes';
import '../styles/components/PipelineView.css';

// Fields a financially blind role must not be able to match through the header search.
export const QUOTE_MONEY_KEYS = ['subtotal', 'tax_rate', 'tax_amount', 'total'] as const;

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  sent: 'Sent',
  accepted: 'Accepted',
  declined: 'Declined',
  expired: 'Expired',
};

// Account-level Client Quotes list — sits alongside Clients/Invoices in
// AccountShell. Modeled directly on ClientsView.tsx: same toolbar-owning
// layout (no Header.tsx in this shell), same Active/Archived/All filter,
// same ConfirmDialog + DataTable actionsColumn (forms are their own pages) wiring.
interface ClientQuotesViewProps {
  /** Show only this project's quotes, inside the project's Billing panel (no page header). */
  projectId?: string;
  /** The project's client, so New quote starts with it chosen. */
  projectClientId?: string | null;
}

const ClientQuotesView: React.FC<ClientQuotesViewProps> = ({ projectId, projectClientId }) => {
  const embedded = !!projectId;
  const navigate = useNavigate();
  const { showError, showSuccess } = useNotifications();
  // Viewers read; money stays hidden from contractors and anyone flagged.
  const { canEdit, canViewFinancials } = useRole();

  const [quotes, setQuotes] = useState<ClientQuote[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [archiveFilter, setArchiveFilter] = useState<ArchiveView>('active');
  const [clientFilter, setClientFilter] = useState<string>('');

  const [archiveTarget, setArchiveTarget] = useState<ClientQuote | null>(null);
  const [archiving, setArchiving] = useState(false);
  const [declineTarget, setDeclineTarget] = useState<ClientQuote | null>(null);
  const [declining, setDeclining] = useState(false);
  const [acceptingId, setAcceptingId] = useState<string | null>(null);
  const [sendingId, setSendingId] = useState<string | null>(null);

  const reload = useCallback(() => {
    setLoading(true);
    setLoadError(null);
    loadClientQuotes({ client_id: clientFilter || undefined, project_id: projectId || undefined, archived: archiveFilter })
      .then(setQuotes)
      // Shown in the page with a Retry, not as a toast that vanishes and
      // leaves what looks like an empty list.
      .catch(err => setLoadError(err ?? new Error('load failed')))
      .finally(() => setLoading(false));
  }, [archiveFilter, clientFilter, projectId, showError]);

  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    loadClients('', 'active').then(setClients).catch(() => {});
  }, []);

  const clientName = (id: string) => clients.find(c => c.id === id)?.name ?? '—';
  // The Client column shows the name, so the header search has to see it on the row too.
  const rows = useMemo(
    () => quotes.map(q => (q.client_name ? q : { ...q, client_name: clients.find(c => c.id === q.client_id)?.name })),
    [quotes, clients],
  );

  // Export button: the quotes listed right now; money columns only for a role that may see money.
  useExport('account-quotes', { rows: loading || loadError ? [] : rows, ctx: { showMoney: canViewFinancials } });

  const openNew = () => navigate(embedded
    ? newBillingDocPath(NEW_CLIENT_QUOTE_PATH, { projectId, clientId: projectClientId })
    : NEW_CLIENT_QUOTE_PATH);
  // Any quote opens on its own page; only a draft is editable there.
  const openQuote = (q: ClientQuote) => navigate(clientQuotePath(q.id));

  const handleSend = async (q: ClientQuote) => {
    setSendingId(q.id);
    try {
      await sendClientQuote(q.id);
      showSuccess(`Quote ${q.quote_number} sent.`);
      reload();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to send quote.');
    } finally {
      setSendingId(null);
    }
  };

  const handleAccept = async (q: ClientQuote) => {
    setAcceptingId(q.id);
    try {
      const { invoice } = await acceptClientQuote(q.id);
      showSuccess(`Quote ${q.quote_number} accepted — invoice ${invoice.invoice_number} created.`);
      reload();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to accept quote.');
    } finally {
      setAcceptingId(null);
    }
  };

  const handleDecline = async () => {
    if (!declineTarget || declining) return;
    setDeclining(true);
    try {
      await declineClientQuote(declineTarget.id);
      showSuccess(`Quote ${declineTarget.quote_number} declined.`);
      setDeclineTarget(null);
      reload();
    } catch (err) {
      showError(err instanceof Error ? err.message : 'Failed to decline quote.');
    } finally {
      setDeclining(false);
    }
  };

  const handleArchive = async () => {
    if (!archiveTarget || archiving) return;
    setArchiving(true);
    try {
      await deleteClientQuote(archiveTarget.id);
      showSuccess(`Quote ${archiveTarget.quote_number} archived.`);
      setArchiveTarget(null);
      reload();
    } catch (err) {
      showError(friendlyDeleteError(err, { record: 'quote', action: 'archive' }));
    } finally {
      setArchiving(false);
    }
  };

  const handleRestore = async (q: ClientQuote) => {
    try {
      await restoreClientQuote(q.id);
      showSuccess(`Quote ${q.quote_number} restored.`);
      reload();
    } catch (err) {
      showError(friendlyDeleteError(err, { record: 'quote', action: 'restore' }));
    }
  };

  // Delete forever is only offered on an archived record (the server refuses
  // otherwise); the dialog says why if it fails.
  const forever = useDeleteConfirm<ClientQuote>({
    record: 'quote',
    perform: row => deleteClientQuote(row.id, true),
    onDone: () => { showSuccess('Quote deleted.'); reload(); },
  });

  const columns: Column<ClientQuote>[] = [
    idColumn<ClientQuote>('clientQuote'),
    { key: 'quote_number', header: 'Quote #', sortable: true },
    ...(embedded ? [] : [{ key: 'client_id', header: 'Client', render: (v: string, row: ClientQuote) => row.client_name ?? clientName(v) }]),
    ...(canViewFinancials ? [
      { key: 'total', header: 'Total', align: 'right' as const, render: (v: number, row: ClientQuote) => formatMoney(v, row.currency) },
    ] : []),
    { key: 'status', header: 'Status', render: v => <span className={`status-pill status-${v}`}>{STATUS_LABELS[v] ?? v}</span> },
    { key: 'valid_until', header: 'Valid Until', render: v => v || '—' },
    actionsColumn<ClientQuote>([
      { key: 'send', icon: 'send', title: 'Send', onClick: handleSend, show: row => canEdit && row.status === 'draft' && sendingId !== row.id },
      { key: 'accept', icon: 'check', title: 'Accept', onClick: handleAccept, show: row => canEdit && row.status === 'sent' && acceptingId !== row.id },
      { key: 'decline', icon: 'x', title: 'Decline', variant: 'danger', onClick: setDeclineTarget, show: row => canEdit && row.status === 'sent' },
      { key: 'edit', icon: 'edit', title: 'Edit', onClick: openQuote, show: row => canEdit && row.status === 'draft' && !row.archived },
      { key: 'archive', icon: 'trash', title: 'Archive', variant: 'danger', onClick: setArchiveTarget, show: row => canEdit && row.status === 'draft' && !row.archived },
      { key: 'delete-forever', icon: 'trash', title: 'Delete forever', variant: 'danger', onClick: forever.request, show: row => canEdit && canDeleteForever(row) },
      { key: 'restore', icon: 'refresh', title: 'Restore', onClick: handleRestore, show: row => canEdit && !!row.archived },
    ]),
  ];

  // The page title is the shell's header title; New, the client filter and the
  // Archived switch sit in the header bar beside it, as on the project screens.
  const barActions: BreadcrumbAction[] = [
    ...(embedded ? [] : [{
      kind: 'filters' as const, key: 'filters',
      selects: [{
        key: 'client', label: 'Client', value: clientFilter, defaultValue: '', onChange: setClientFilter,
        options: [{ value: '', label: 'All clients' }, ...clients.map(c => ({ value: c.id, label: c.name }))],
      }],
    }]),
    ...(canEdit ? [{ kind: 'primary' as const, key: 'new', icon: 'plus', label: embedded ? 'New quote' : 'New Quote', onClick: openNew }] : []),
  ];

  return (
    <div className={embedded ? 'pb-list' : 'wt-container fade-in'}>
        {embedded ? (
          <div className="pb-toolbar">
            <ArchivedToggle checked={isArchiveView(archiveFilter)} onChange={on => setArchiveFilter(toArchiveView(on))} />
            <BreadcrumbBar actions={barActions} />
          </div>
        ) : (
          <AccountBar
            view="client-quotes"
            archived={{ checked: isArchiveView(archiveFilter), onChange: on => setArchiveFilter(toArchiveView(on)) }}
            actions={<BreadcrumbBar actions={barActions} />}
            exportKey="account-quotes"
          />
        )}
        {!embedded && archiveFilter !== 'archived' && !loadError && !loading && quotes.length > 0 && (
          <AccountMetrics label="Quotes summary" metrics={quoteMetrics(quotes, canViewFinancials)} />
        )}

        {loadError ? (
          <LoadErrorBox what="quotes" error={loadError} onRetry={reload} retrying={loading} />
        ) : !loading && quotes.length === 0 ? (
          <EmptyState
            icon="document"
            title={archiveFilter === 'archived' ? 'No archived quotes' : 'No quotes yet'}
            description={archiveFilter === 'archived'
              ? 'Quotes you archive will show up here.'
              : embedded
                ? 'Quotes for this project will show up here.'
                : 'Create a quote for a client before any project exists.'}
            action={canEdit && archiveFilter !== 'archived' ? (
              <button type="button" className="pl-new-btn" onClick={openNew}>
                <Icon name="plus" size={14} /> {embedded ? 'New quote' : 'Create first quote'}
              </button>
            ) : undefined}
          />
        ) : (
          <DataTable<ClientQuote>
            recordList={{ kind: 'clientQuote' }}
            data={rows}
            columns={columns}
            loading={loading}
            filterOmit={canViewFinancials ? undefined : QUOTE_MONEY_KEYS}
            hoverable
            onRowClick={openQuote}
            emptyMessage="No quotes match your search."
          />
        )}

      <ConfirmDialog
        {...forever.dialogProps}
        title="Delete this quote forever?"
        subject={forever.target?.quote_number}
        description="This cannot be undone."
        consequences={['The quote and its line items']}
        confirmLabel="Delete forever"
      />
      <ConfirmDialog
        open={archiveTarget !== null}
        onClose={() => setArchiveTarget(null)}
        onConfirm={handleArchive}
        title={`Archive quote ${archiveTarget?.quote_number}?`}
        description="It will be hidden from the active list but not permanently deleted."
        confirmLabel="Archive"
        loading={archiving}
      />

      <ConfirmDialog
        open={declineTarget !== null}
        onClose={() => setDeclineTarget(null)}
        onConfirm={handleDecline}
        title={`Decline quote ${declineTarget?.quote_number}?`}
        description="The client will not be able to accept this quote afterward."
        confirmLabel="Decline"
        loading={declining}
      />
    </div>
  );
};

export default ClientQuotesView;
